import auth from '@/auth';
import { openModal, toastWarning } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import store from '@/store';
import { isExternalRedirect, navigateToExternalRedirect, sanitizeRedirect } from '@/utils/redirects';

/**
 * @type {NavigationGuard}
 */
export const beforeSignupOrLogin = async (to, from, next) => {
  const tokenExists = auth.isLoggedIn();
  const isAnonymousUser = auth.isAnonymousUser();
  const isPairing = !!to.query.pairing;
  const isFigmaAuthRequired = !!to.query.request_oauth;

  // wait a second, there might just be a user over there...
  if (tokenExists) {
    try {
      // let's see... can we login?
      const user = await store.dispatch('users/fetchOne', { id: 'me' });

      if (to.query.redirect) {
        const redirect = sanitizeRedirect(to.query.redirect);
        if (isExternalRedirect(redirect)) {
          if (isFigmaAuthRequired && !user.enabled_figma_api) {
            return next({ name: 'figma-generate-token', query: { callbackUrl: redirect } });
          }
          navigateToExternalRedirect(redirect);
          return;
        }
        if (redirect !== '/') {
          return next(redirect);
        }
      }

      let name = 'root';
      let query = {};

      // redirect to relevant page.
      if (!isAnonymousUser && isPairing) {
        if (isFigmaAuthRequired && !user.enabled_figma_api) {
          return next({ name: 'figma-generate-token', query: { callbackUrl: to.fullPath } });
        }

        name = 'paired';
        query = to.query;
      }
      return next({ name, query });
    } catch (err) {
      // nope!
      errorHandler.captureException(err);
    }
  }

  if (['figma-panel', 'dev-mode'].includes(to.query.figma_mode)) {
    store.dispatch('webappSystem/updateOriginDevMode', true);
  }

  if (to.query.intent === 'devmode') {
    store.dispatch('webappSystem/updateIntentDevMode', true);
  }

  // ALWAYS KEEP IN THE END OF THE FUNCTION
  next();
};

/**
 * @type {NavigationGuard}
 */
export const beforeEnterModal = (props = {}) => {
  return (to, from, next) => {
    let onCloseRedirect = props.onCloseRedirect || from || '/';
    if (props.calcRedirect) {
      const calcResult = props.calcRedirect(to, from);
      onCloseRedirect = calcResult || onCloseRedirect;
      props.forceCloseRedirect = !!calcResult;
    }
    openModal({ ...props, onCloseRedirect });
    next();
  };
};

/**
 * @type {NavigationGuard}
 */
export const beforeEnterNewTeam = async (to, from, next) => {
  let userMemberships = store.state.teamMemberships.user || [];
  if (userMemberships.length === 0) {
    try {
      await store.dispatch('teamMemberships/fetchAllUserMemberships', { id: 'me' });
      userMemberships = store.state.teamMemberships.user || [];
    } catch (err) {
      errorHandler.captureException(err);
    }
  }

  const hasPaidTeam = userMemberships.some((m) => m.team_plan && m.team_plan !== 'Free');
  if (userMemberships.length > 0 && !hasPaidTeam) {
    toastWarning('Free users cannot create new teams. Upgrade your plan to add more teams.');
    return next(from?.name ? false : { name: 'root' });
  }
  return beforeEnterModal({ name: 'new-team' })(to, from, next);
};
